"use client"

import { useState, useMemo, useEffect, useRef } from "react"
import { X, Plus, ChevronDown, ChevronUp, Filter } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Progress } from "@/components/ui/progress"
import { API_BASE_URL } from "@/lib/api"
import { indiaPersonas, type Persona } from "@/lib/india-personas"
import { CustomInstructions } from "@/components/custom-instructions"

// Extended interface for create persona form
interface ExtendedPersona extends Partial<Persona> {
  secondary_languages?: string[]
  speaking_style?: string[]
  emotion?: string
  secondary_intent?: string[]
  medical_context?: string[]
  stress_level?: string
  risk_flags?: string[]
  description?: string
  sample_utterances?: string[]
  patient_system_prompt?: string
  recommended_evals?: string[]
  notes?: string
}

export function CallSimulation() {
  const [selectedPersonas, setSelectedPersonas] = useState<Persona[]>([])
  const [isLibraryOpen, setIsLibraryOpen] = useState(false)
  const [isCreatePersonaOpen, setIsCreatePersonaOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [hoveredPersona, setHoveredPersona] = useState<Persona | null>(null)
  const [checkedPersonas, setCheckedPersonas] = useState<Set<string>>(new Set())
  const [scriptMode, setScriptMode] = useState<"none" | "fixed" | "checkpoint">("none")
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [speakingSpeed, setSpeakingSpeed] = useState([1.0])
  const [volume, setVolume] = useState([1.0])
  const [noiseIntensity, setNoiseIntensity] = useState([0.5])
  const [simulationStatus, setSimulationStatus] = useState<string>("")
  
  // WebSocket ref
  const wsRef = useRef<WebSocket | null>(null)
  // Session ID for this simulation
  const sessionIdRef = useRef<string | null>(null)

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)

  const [filterLanguage, setFilterLanguage] = useState<string[]>([])
  const [filterEmotion, setFilterEmotion] = useState<string[]>([])
  const [filterIntent, setFilterIntent] = useState<string[]>([])
  const [filterStressLevel, setFilterStressLevel] = useState<string[]>([])
  const [filterRiskFlags, setFilterRiskFlags] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<"relevance" | "name" | "recent">("relevance")

  const [expandedFilters, setExpandedFilters] = useState({
    language: true,
    emotion: true,
    intent: true,
    stress: true,
    risk: true,
  })

  const [personaLibrary, setPersonaLibrary] = useState<Persona[]>([])

  const [newPersona, setNewPersona] = useState<ExtendedPersona>({
    name: "",
    language: "",
    secondary_languages: [],
    speaking_style: [],
    emotion: "",
    intent: "",
    secondary_intent: [],
    medical_context: [],
    stress_level: "Low",
    risk_flags: [],
    description: "",
    sample_utterances: [],
    patient_system_prompt: "",
    recommended_evals: [],
    notes: "",
  })

  const [patientModel, setPatientModel] = useState<string>("")
  const [voiceName, setVoiceName] = useState<string>("")
  const [languageAccent, setLanguageAccent] = useState<string>("en-us")
  const [speechClarity, setSpeechClarity] = useState<string>("high")
  const [interruptionBehavior, setInterruptionBehavior] = useState<string>("normal")
  const [noiseType, setNoiseType] = useState<string>("none")
  const [callStability, setCallStability] = useState<string>("stable")
  const [simulationPrompt, setSimulationPrompt] = useState<string>("")
  const [fixedScript, setFixedScript] = useState<string>("")
  const [checkpointScript, setCheckpointScript] = useState<string>("")
  
  // Store audio chunks and metadata for upload
  const audioChunksRef = useRef<Blob[]>([])
  const simulationMetadataRef = useRef<any>(null)

  // Load personas from localStorage on mount
  useEffect(() => {
    try {
      const savedPersonas = localStorage.getItem('voiceAgent.personaLibrary')
      if (savedPersonas) {
        const parsed = JSON.parse(savedPersonas)
        setPersonaLibrary(parsed)
        console.log('[CallSimulation] Loaded', parsed.length, 'personas from localStorage')
      } else {
        // If no saved personas, use default India personas
        setPersonaLibrary(indiaPersonas)
        console.log('[CallSimulation] No saved personas found, using default India personas')
      }
    } catch (error) {
      console.error('[CallSimulation] Failed to load personas from localStorage:', error)
      // Fallback to default personas
      setPersonaLibrary(indiaPersonas)
    }
  }, [])

  // Save personas to localStorage whenever they change
  useEffect(() => {
    if (personaLibrary.length > 0) {
      try {
        localStorage.setItem('voiceAgent.personaLibrary', JSON.stringify(personaLibrary))
        console.log('[CallSimulation] Saved', personaLibrary.length, 'personas to localStorage')
      } catch (error) {
        console.error('[CallSimulation] Failed to save personas to localStorage:', error)
      }
    }
  }, [personaLibrary])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 250)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const removePersona = (id: string) => {
    setSelectedPersonas(selectedPersonas.filter((p) => p.id !== id))
  }

  const addSelectedPersonas = () => {
    const personasToAdd = personaLibrary.filter((p) => checkedPersonas.has(p.id))
    const newPersonas = personasToAdd.filter((p) => !selectedPersonas.find((sp) => sp.id === p.id))
    setSelectedPersonas([...selectedPersonas, ...newPersonas])
    setCheckedPersonas(new Set())
    setIsLibraryOpen(false)
  }

  const togglePersonaCheck = (id: string) => {
    const newChecked = new Set(checkedPersonas)
    if (newChecked.has(id)) {
      newChecked.delete(id)
    } else {
      newChecked.add(id)
    }
    setCheckedPersonas(newChecked)
  }

  const handleCreatePersona = () => {
    const persona: Persona = {
      id: `custom-${Date.now()}`,
      name: newPersona.name || "Unnamed Persona",
      category: newPersona.description || "Custom",
      type: "Custom",
      intent: newPersona.intent || "",
      language: newPersona.language || "",
      emotionalState: newPersona.emotion || "",
      behaviorTraits: newPersona.speaking_style?.join(", ") || "",
      riskFlags: newPersona.risk_flags?.join(", ") || "",
      riskLevel: newPersona.stress_level || "Low",
      systemPrompt: newPersona.patient_system_prompt || "",
    }

    setPersonaLibrary((prev) => [...prev, persona])
    setCheckedPersonas((prev) => new Set([...prev, persona.id]))

    setNewPersona({
      name: "",
      language: "",
      secondary_languages: [],
      speaking_style: [],
      emotion: "",
      intent: "",
      secondary_intent: [],
      medical_context: [],
      stress_level: "Low",
      risk_flags: [],
      description: "",
      sample_utterances: [],
      patient_system_prompt: "",
      recommended_evals: [],
      notes: "",
    })
    setIsCreatePersonaOpen(false)
  }

  const toggleFilter = (current: string[], setter: (val: string[]) => void, value: string) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value))
    } else {
      setter([...current, value])
    }
  }

  const clearAllFilters = () => {
    setFilterLanguage([])
    setFilterEmotion([])
    setFilterIntent([])
    setFilterStressLevel([])
    setFilterRiskFlags([])
    setSearchQuery("")
    setDebouncedSearch("")
  }

  const filteredPersonas = useMemo(() => {
    const filtered = personaLibrary.filter((persona) => {
      const personaAny = persona as any
      const matchesSearch =
        persona.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (personaAny.description || personaAny.category || '').toLowerCase().includes(debouncedSearch.toLowerCase())
      const matchesLanguage = filterLanguage.length === 0 || filterLanguage.includes(persona.language)
      const matchesEmotion = filterEmotion.length === 0 || filterEmotion.includes(personaAny.emotion || personaAny.emotionalState)
      const matchesIntent = filterIntent.length === 0 || filterIntent.includes(persona.intent)
      const matchesStress = filterStressLevel.length === 0 || filterStressLevel.includes(personaAny.stress_level || personaAny.riskLevel)
      
      const matchesRisk = (() => {
        if (filterRiskFlags.length === 0) return true
        const riskData = personaAny.risk_flags || personaAny.riskFlags
        if (Array.isArray(riskData)) {
          return riskData.some((flag) => filterRiskFlags.includes(flag))
        } else if (typeof riskData === 'string' && riskData) {
          return riskData.split(',').some((flag) => filterRiskFlags.includes(flag.trim()))
        }
        return false
      })()

      return matchesSearch && matchesLanguage && matchesEmotion && matchesIntent && matchesStress && matchesRisk
    })

    // Sort
    if (sortBy === "name") {
      filtered.sort((a, b) => a.name.localeCompare(b.name))
    } else if (sortBy === "recent") {
      filtered.reverse()
    }

    return filtered
  }, [
    personaLibrary,
    debouncedSearch,
    filterLanguage,
    filterEmotion,
    filterIntent,
    filterStressLevel,
    filterRiskFlags,
    sortBy,
  ])

  const selectAllVisible = () => {
    const allIds = new Set([...checkedPersonas, ...filteredPersonas.map((p) => p.id)])
    setCheckedPersonas(allIds)
  }

  const clearSelection = () => {
    setCheckedPersonas(new Set())
  }

  const handleRunSimulation = () => {
    try {
      // Get config from localStorage and parse it
      const configStr = localStorage.getItem('voiceAgent.config')
      let agentName = null
      let testerAgentName = 'default-tester'
      let host = null
      
      if (configStr) {
        try {
          const config = JSON.parse(configStr)
          agentName = config.agentName
          testerAgentName = config.testerAgentName || 'default-tester'
          host = config.host
        } catch (e) {
          console.error('[CallSimulation] Failed to parse config:', e)
        }
      }
      
      if (!agentName) {
        console.error('[CallSimulation] No agent name found in localStorage')
        alert('Agent name not found. Please configure the agent first.')
        return
      }

      if (!host) {
        console.error('[CallSimulation] No host found in localStorage')
        alert('Host not found. Please configure the agent first.')
        return
      }

      // Validate that at least one persona is selected
      if (selectedPersonas.length === 0) {
        console.error('[CallSimulation] No personas selected')
        alert('Please select at least one persona before running the simulation.')
        return
      }

      // Extract persona names from selected personas
      const personaNames = selectedPersonas.map(p => p.name).join(',')
      
      // Generate unique session ID for this simulation
      const sessionId = `sim_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
      sessionIdRef.current = sessionId
      
      console.log('[CallSimulation] Starting simulation for agent:', agentName)
      console.log('[CallSimulation] Tester agent:', testerAgentName)
      console.log('[CallSimulation] Selected personas:', personaNames)
      console.log('[CallSimulation] Host:', host)
      console.log('[CallSimulation] Session ID:', sessionId)
      
      // Build query parameters
      const queryParams = new URLSearchParams({
        tester_name: testerAgentName,
        persona: personaNames,
        session_id: sessionId,
      })
      
      const websocketUrl = `ws://${host}/agents/simulation/${agentName}?${queryParams.toString()}`
      
      console.log('[CallSimulation] Connecting to WebSocket:', websocketUrl)
      
      // Create WebSocket connection
      const ws = new WebSocket(websocketUrl)
      wsRef.current = ws
      
      ws.onopen = () => {
        console.log('[CallSimulation] WebSocket connected')
        setIsRunning(true)
        setProgress(0)
        setSimulationStatus('Running simulation...')
        
        // Send initial configuration with all custom parameters
        ws.send(JSON.stringify({ 
          event: 'start',
          personas: selectedPersonas.map((p:any) => ({
            id: p.id,
            name: p.name,
            language: p.language,
            secondary_languages: p.secondary_languages,
            speaking_style: p.speaking_style,
            emotion: p.emotion,
            intent: p.intent,
            secondary_intent: p.secondary_intent,
            medical_context: p.medical_context,
            stress_level: p.stress_level,
            risk_flags: p.risk_flags,
            description: p.description,
            sample_utterances: p.sample_utterances,
            patient_system_prompt: p.patient_system_prompt,
            recommended_evals: p.recommended_evals,
            notes: p.notes,
          })),
          tester_agent_name: testerAgentName,
          config: {
            // Patient model & voice
            patient_model: patientModel,
            voice_name: voiceName,
            language_accent: languageAccent,
            
            // Speech characteristics
            speaking_speed: speakingSpeed[0],
            volume: volume[0],
            speech_clarity: speechClarity,
            interruption_behavior: interruptionBehavior,
            
            // Environment simulation
            noise_type: noiseType,
            noise_intensity: noiseIntensity[0],
            call_stability: callStability,
            
            // Simulation prompt & scripts
            simulation_prompt: simulationPrompt,
            script_mode: scriptMode,
            fixed_script: scriptMode === 'fixed' ? fixedScript : undefined,
            checkpoint_script: scriptMode === 'checkpoint' ? checkpointScript : undefined,
          }
        }))
      }
      
      ws.onmessage = async (event) => {
        console.log('[CallSimulation] WebSocket message received:', typeof event.data === 'string' ? event.data : 'Binary data')
        
        // Handle binary data (audio file sent as Blob)
        if (event.data instanceof Blob) {
          console.log('[CallSimulation] Received audio file as Blob:', event.data.size, 'bytes')
          
          // This is the complete audio file, upload it immediately
          if (event.data.size > 0 && sessionIdRef.current) {
            setSimulationStatus('Uploading audio to evals...')
            try {
              await uploadAudioBlob(event.data, agentName, testerAgentName, sessionIdRef.current)
              setSimulationStatus('✅ Audio uploaded successfully! Check Evals tab.')
              setIsRunning(false)
            } catch (error) {
              console.error('[CallSimulation] Failed to upload audio blob:', error)
              setSimulationStatus('❌ Failed to upload audio')
              setIsRunning(false)
            }
          }
          return
        }
        
        try {
          const data = JSON.parse(event.data)
          
          // Handle different message types
          if (data.event === 'progress') {
            setProgress(data.progress || 0)
          } else if (data.event === 'metadata') {
            // Store metadata for upload
            simulationMetadataRef.current = data.metadata
            console.log('[CallSimulation] Received metadata:', data.metadata)
          } else if (data.event === 'audio_data') {
            // Handle audio sent as base64 encoded data
            console.log('[CallSimulation] Received audio as base64 data')
            if (data.audio_base64 && sessionIdRef.current) {
              setSimulationStatus('Uploading audio to evals...')
              try {
                // Convert base64 to blob
                const byteCharacters = atob(data.audio_base64)
                const byteNumbers = new Array(byteCharacters.length)
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const audioBlob = new Blob([byteArray], { type: 'audio/wav' })
                
                await uploadAudioBlob(audioBlob, agentName, testerAgentName, sessionIdRef.current)
                setSimulationStatus('✅ Audio uploaded successfully! Check Evals tab.')
                setIsRunning(false)
              } catch (error) {
                console.error('[CallSimulation] Failed to process base64 audio:', error)
                setSimulationStatus('❌ Failed to process audio')
                setIsRunning(false)
              }
            }
          } else if (data.event === 'completed') {
            setProgress(100)
            console.log('[CallSimulation] Simulation completed')
            console.log('[CallSimulation] Completion data:', data)
            
            // Check if wav_data is included in completion message
            if (data.wav_data && sessionIdRef.current) {
              console.log('[CallSimulation] WAV data received in completion message')
              console.log('[CallSimulation] WAV file:', data.wav_file)
              console.log('[CallSimulation] Size:', data.size, 'bytes')
              
              setSimulationStatus('Uploading audio to evals...')
              
              try {
                // Convert base64 to blob
                const byteCharacters = atob(data.wav_data)
                const byteNumbers = new Array(byteCharacters.length)
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const audioBlob = new Blob([byteArray], { type: 'audio/wav' })
                
                console.log('[CallSimulation] Converted to blob, size:', audioBlob.size, 'bytes')
                
                // Upload to batch API
                await uploadAudioBlob(audioBlob, agentName, testerAgentName, sessionIdRef.current)
                
                setSimulationStatus('✅ Audio uploaded successfully! Check Evals tab.')
                setIsRunning(false)
                
              } catch (error) {
                console.error('[CallSimulation] Failed to process WAV data:', error)
                setSimulationStatus('❌ Failed to upload audio')
                setIsRunning(false)
              }
            } else {
              console.log('[CallSimulation] No WAV data in completion message')
              setSimulationStatus('✅ Simulation completed')
              setIsRunning(false)
            }
          } else if (data.event === 'error') {
            console.error('[CallSimulation] Simulation error:', data.error)
            setSimulationStatus(`❌ Simulation error: ${data.error}`)
            setIsRunning(false)
          }
        } catch (err) {
          console.error('[CallSimulation] Failed to parse WebSocket message:', err)
        }
      }
      
      ws.onerror = (error) => {
        console.error('[CallSimulation] WebSocket error:', error)
        setSimulationStatus('❌ Connection error')
        setIsRunning(false)
      }
      
      ws.onclose = () => {
        console.log('[CallSimulation] WebSocket closed')
        if (isRunning) {
          setSimulationStatus('❌ Connection closed')
          setIsRunning(false)
        }
      }
      
    } catch (error) {
      console.error('[CallSimulation] Error starting simulation:', error)
      setSimulationStatus('❌ Failed to start simulation')
      setIsRunning(false)
    }
  }

  // Function to upload audio blob directly to batch API
  const uploadAudioBlob = async (
    audioBlob: Blob, 
    agentName: string, 
    testerAgentName: string,
    sessionId: string
  ) => {
    try {
      console.log('[CallSimulation] Uploading audio blob...')
      console.log('[CallSimulation] Audio blob size:', audioBlob.size, 'bytes')
      console.log('[CallSimulation] Session ID:', sessionId)
      
      // Create form data
      const formData = new FormData()
      formData.append('eval_suite_id', 'Simulation Agent')
      formData.append('agent_version', `${agentName || 'agent'}-preview-${Date.now()}`)
      formData.append('files', audioBlob, `${sessionId}.wav`)
      
      // Upload to batch API
      const uploadUrl = `${API_BASE_URL}/batch/upload`
      console.log('[CallSimulation] Uploading to:', uploadUrl)
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true',
        }
      })
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }
      
      const result = await response.json()
      console.log('[CallSimulation] Upload response:', result)
      
      // Check if batch_id is returned
      if (result.batch_id) {
        const batchId = result.batch_id
        console.log('[CallSimulation] Batch created:', batchId)
        console.log('[CallSimulation] Status:', result.status)
        console.log('[CallSimulation] Upload successful! Batch ID:', batchId)
      } else {
        console.log('[CallSimulation] Upload successful! Session ID:', sessionId)
      }
      
    } catch (error) {
      console.error('[CallSimulation] Upload failed:', error)
      throw error // Re-throw to be caught by caller
    }
  }

  // Function to upload audio recording to batch API (legacy - for fallback)
  const uploadAudioRecording = async (agentName: string, testerAgentName: string) => {
    try {
      console.log('[CallSimulation] Uploading audio recording...')
      
      // Combine all audio chunks into a single blob
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      console.log('[CallSimulation] Audio blob size:', audioBlob.size, 'bytes')
      
      // Create form data
      const formData = new FormData()
      formData.append('eval_suite_id', 'Simulation Agent')
      formData.append('agent_version', `${agentName || 'agent'}-preview-${Date.now()}`)
      formData.append('files', audioBlob, `simulation-${testerAgentName}-${Date.now()}.webm`)
      
      // Upload to batch API
      const uploadUrl = `${API_BASE_URL}/batch/upload`
      console.log('[CallSimulation] Uploading to:', uploadUrl)
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true',
        }
      })
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }
      
      const result = await response.json()
      console.log('[CallSimulation] Upload response:', result)
      
      // Clear audio chunks
      audioChunksRef.current = []
      
      // Check if batch_id is returned
      if (result.batch_id) {
        const batchId = result.batch_id
        console.log('[CallSimulation] Batch created:', batchId)
        console.log('[CallSimulation] Status:', result.status)
        
        alert(
          `✅ Simulation audio uploaded successfully!\n\n` +
          `Batch ID: ${batchId}\n` +
          `Status: ${result.status}\n\n` +
          `The batch is being processed automatically.\n` +
          `Refresh the Evals page in a few moments to see results.`
        )
      } else {
        alert(
          `✅ Simulation audio uploaded!\n\n` +
          `Response: ${JSON.stringify(result)}\n\n` +
          `Refresh the Evals page to view results.`
        )
      }
      
    } catch (error) {
      console.error('[CallSimulation] Upload failed:', error)
      alert(`❌ Failed to upload audio recording.\n\nError: ${error}\n\nCheck console for details.`)
    }
  }

  // Function to trigger batch processing (no longer needed - auto-processing is default)
  const triggerBatchProcessing = async (batchId: string) => {
    try {
      console.log('[CallSimulation] Checking batch processing status for:', batchId)
      
      const statusUrl = `${API_BASE_URL}/batch/${batchId}`
      const response = await fetch(statusUrl, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
        }
      })
      
      if (response.ok) {
        const status = await response.json()
        console.log('[CallSimulation] Batch status:', status)
      }
    } catch (error) {
      console.warn('[CallSimulation] Could not check batch status:', error)
    }
  }

  // Function to upload audio from URL (if backend provides URL)
  const uploadAudioFromUrl = async (
    audioUrl: string, 
    agentName: string, 
    testerAgentName: string,
    originalFilename?: string
  ) => {
    try {
      console.log('[CallSimulation] Fetching audio from URL:', audioUrl)
      
      // Fetch audio from URL
      const audioResponse = await fetch(audioUrl, {
        headers: {
          'ngrok-skip-browser-warning': 'true',
        }
      })
      
      if (!audioResponse.ok) {
        throw new Error(`Failed to fetch audio: ${audioResponse.status} ${audioResponse.statusText}`)
      }
      
      const audioBlob = await audioResponse.blob()
      console.log('[CallSimulation] Audio blob size:', audioBlob.size, 'bytes')
      
      if (audioBlob.size === 0) {
        throw new Error('Audio file is empty')
      }
      
      // Create form data
      const formData = new FormData()
      formData.append('eval_suite_id', 'Simulation Agent')
      formData.append('agent_version', `${agentName || 'agent'}-preview-${Date.now()}`)
      
      // Use provided filename or extract from URL
      const filename = originalFilename || audioUrl.split('/').pop() || `simulation-${testerAgentName}-${Date.now()}.wav`
      formData.append('files', audioBlob, filename)
      
      // Upload to batch API
      const uploadUrl = `${API_BASE_URL}/batch/upload`
      console.log('[CallSimulation] Uploading to:', uploadUrl)
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'ngrok-skip-browser-warning': 'true',
        }
      })
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }
      
      const result = await response.json()
      console.log('[CallSimulation] Upload from URL response:', result)
      
      // Check if batch_id is returned
      if (result.batch_id) {
        const batchId = result.batch_id
        console.log('[CallSimulation] Batch created:', batchId)
        
        alert(
          `✅ Simulation audio uploaded successfully!\n\n` +
          `Batch ID: ${batchId}\n` +
          `Status: ${result.status}\n\n` +
          `Processing automatically. Refresh Evals page to see results.`
        )
      } else {
        alert(`✅ Simulation audio uploaded!\n\nRefresh the Evals page to view results.`)
      }
      
    } catch (error) {
      console.error('[CallSimulation] Upload from URL failed:', error)
      alert(`❌ Failed to upload audio.\n\nError: ${error}\n\nCheck console for details.`)
    }
  }

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close()
      }
    }
  }, [])

  const uniqueLanguages = useMemo(() => [...new Set(personaLibrary.map((p) => p.language))].sort(), [personaLibrary])
  const uniqueEmotions = useMemo(() => [...new Set(personaLibrary.map((p) => (p as any).emotion || (p as any).emotionalState))].sort(), [personaLibrary])
  const uniqueIntents = useMemo(() => [...new Set(personaLibrary.map((p) => p.intent))].sort(), [personaLibrary])
  const uniqueRiskFlags = useMemo(() => {
    const flags = new Set<string>()
    personaLibrary.forEach((p) => {
      const riskData = (p as any).risk_flags || (p as any).riskFlags
      if (Array.isArray(riskData)) {
        riskData.forEach((f) => flags.add(f))
      } else if (typeof riskData === 'string' && riskData) {
        // Split string by comma or other delimiter if needed
        riskData.split(',').forEach((f) => flags.add(f.trim()))
      }
    })
    return [...flags].sort()
  }, [personaLibrary])

  const getVoiceOptions = () => {
    if (patientModel === "sarvam-ai") {
      return {
        female: ["Anushka", "Manisha", "Vidya", "Arya"],
        male: ["Abhilash", "Karun", "Hitesh"],
      }
    } else if (patientModel === "google-voices") {
      return {
        female: [
          "Achernar",
          "Aoede",
          "Autonoe",
          "Callirrhoe",
          "Despina",
          "Erinome",
          "Gacrux",
          "Kore",
          "Laomedeia",
          "Leda",
          "Pulcherrima",
          "Sulafat",
          "Vindemiatrix",
          "Zephyr",
        ],
        male: [
          "Achird",
          "Algenib",
          "Algieba",
          "Alnilam",
          "Charon",
          "Enceladus",
          "Fenrir",
          "Iapetus",
          "Orus",
          "Puck",
          "Rasalgethi",
          "Sadachbia",
          "Sadaltager",
          "Schedar",
          "Umbriel",
          "Zubenelgenubi",
        ],
      }
    }
    return { female: [], male: [] }
  }

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto p-8 space-y-8">
        {/* SECTION 1: SELECTED PERSONAS */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-black">Selected Personas</h2>
            <div className="flex items-center gap-3">
              <Input placeholder="Search selected personas..." className="w-64 bg-white border-gray-300" />
              <Button onClick={() => setIsLibraryOpen(true)} className="bg-black text-white hover:bg-black/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Personas
              </Button>
            </div>
          </div>

          {selectedPersonas.length === 0 ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <p className="text-muted-foreground mb-4">No personas selected yet</p>
              <Button
                onClick={() => setIsLibraryOpen(true)}
                variant="outline"
                className="bg-white border-black text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Persona
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedPersonas.map((persona) => (
                <div key={persona.id} className="border border-gray-300 rounded-lg p-4 bg-white relative">
                  <button
                    onClick={() => removePersona(persona.id)}
                    className="absolute top-2 right-2 text-gray-500 hover:text-black"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="space-y-2 text-sm">
                    <div className="font-semibold text-black text-base mb-3">{persona.name}</div>
                    <div>
                      <span className="text-gray-600">Category:</span>{" "}
                      <span className="text-black text-xs">{persona.category}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Language:</span>{" "}
                      <span className="text-black">{persona.language}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Emotional State:</span>{" "}
                      <span className="text-black">{persona.emotionalState}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Intent:</span>{" "}
                      <span className="text-black">{persona.intent}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Risk Level:</span>{" "}
                      <span className="text-black">{persona.riskLevel}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 2: PATIENT VOICE CONFIGURATION */}
        <div className="space-y-4 border-t border-gray-200 pt-8">
          <h2 className="text-xl font-semibold text-black">Patient Voice Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Row 1 */}
            <div className="space-y-2">
              <Label htmlFor="patient-model" className="text-black font-medium">
                Patient Model
              </Label>
              <Select
                value={patientModel}
                onValueChange={(value) => {
                  setPatientModel(value)
                  setVoiceName("") // Reset voice name when model changes
                }}
              >
                <SelectTrigger id="patient-model" className="bg-white border-gray-300">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {/* <SelectItem value="gpt-4o-realtime">OpenAI – gpt-4o-realtime</SelectItem> */}
                  {/* <SelectItem value="gpt-4o-mini-realtime">OpenAI – gpt-4o-mini-realtime</SelectItem> */}
                  <SelectItem value="sarvam-ai">Sarvam AI</SelectItem>
                  {/* <SelectItem value="google-voices">Google Voices</SelectItem> */}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="voice-name" className="text-black font-medium">
                Voice Name
              </Label>
              <Select
                value={voiceName}
                onValueChange={setVoiceName}
                disabled={!patientModel || (patientModel !== "sarvam-ai" && patientModel !== "google-voices")}
              >
                <SelectTrigger id="voice-name" className="bg-white border-gray-300">
                  <SelectValue placeholder={!patientModel ? "Select model first" : "Select voice"} />
                </SelectTrigger>
                <SelectContent>
                  {(patientModel === "sarvam-ai" || patientModel === "google-voices") && (
                    <>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-semibold text-gray-500 px-2 py-1.5">
                          Female Voices
                        </SelectLabel>
                        {getVoiceOptions().female.map((voice) => (
                          <SelectItem key={voice} value={voice.toLowerCase()}>
                            {voice}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-semibold text-gray-500 px-2 py-1.5">
                          Male Voices
                        </SelectLabel>
                        {getVoiceOptions().male.map((voice) => (
                          <SelectItem key={voice} value={voice.toLowerCase()}>
                            {voice}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="language-accent" className="text-black font-medium">
                Language / Accent
              </Label>
              <Select value={languageAccent} onValueChange={setLanguageAccent}>
                <SelectTrigger id="language-accent" className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en-us">English (US)</SelectItem>
                  <SelectItem value="en-uk">English (UK)</SelectItem>
                  <SelectItem value="en-in">English (India)</SelectItem>
                  <SelectItem value="hi-in">Hindi (India)</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Row 2 */}
            <div className="space-y-2">
              <Label htmlFor="speaking-speed" className="text-black font-medium flex items-center justify-between">
                <span>Speaking Speed</span>
                <span className="text-sm font-normal text-gray-600">{speakingSpeed[0].toFixed(1)}x</span>
              </Label>
              <Slider
                id="speaking-speed"
                min={0.5}
                max={2.0}
                step={0.1}
                value={speakingSpeed}
                onValueChange={setSpeakingSpeed}
                className="[&_[role=slider]]:bg-black [&_[role=slider]]:border-black"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="volume" className="text-black font-medium flex items-center justify-between">
                <span>Volume</span>
                <span className="text-sm font-normal text-gray-600">{volume[0].toFixed(1)}</span>
              </Label>
              <Slider
                id="volume"
                min={0}
                max={1}
                step={0.1}
                value={volume}
                onValueChange={setVolume}
                className="[&_[role=slider]]:bg-black [&_[role=slider]]:border-black"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="speech-clarity" className="text-black font-medium">
                Speech Clarity
              </Label>
              <Select value={speechClarity} onValueChange={setSpeechClarity}>
                <SelectTrigger id="speech-clarity" className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Row 3 */}
            <div className="space-y-2">
              <Label htmlFor="interruption" className="text-black font-medium">
                Interruption Behavior
              </Label>
              <Select value={interruptionBehavior} onValueChange={setInterruptionBehavior}>
                <SelectTrigger id="interruption" className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="frequent">Frequent</SelectItem>
                  <SelectItem value="aggressive">Aggressive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* SECTION 3: ENVIRONMENT SIMULATION */}
        <div className="space-y-4 border-t border-gray-200 pt-8">
          <h2 className="text-xl font-semibold text-black">Environment Simulation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="noise-type" className="text-black font-medium">
                Background Noise Type
              </Label>
              <Select value={noiseType} onValueChange={setNoiseType}>
                <SelectTrigger id="noise-type" className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="traffic">Traffic</SelectItem>
                  <SelectItem value="crowd">Crowd</SelectItem>
                  <SelectItem value="home">Home</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="noise-intensity" className="text-black font-medium flex items-center justify-between">
                <span>Noise Intensity</span>
                <span className="text-sm font-normal text-gray-600">{noiseIntensity[0].toFixed(1)}</span>
              </Label>
              <Slider
                id="noise-intensity"
                min={0}
                max={1}
                step={0.1}
                value={noiseIntensity}
                onValueChange={setNoiseIntensity}
                className="[&_[role=slider]]:bg-black [&_[role=slider]]:border-black"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="call-stability" className="text-black font-medium">
                Call Stability
              </Label>
              <Select value={callStability} onValueChange={setCallStability}>
                <SelectTrigger id="call-stability" className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stable">Stable</SelectItem>
                  <SelectItem value="minor-drops">Minor Drops</SelectItem>
                  <SelectItem value="unstable">Unstable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* SECTION 4: SIMULATION PROMPT */}
        <div className="space-y-4 border-t border-gray-200 pt-8">
          <h2 className="text-xl font-semibold text-black">Simulation Prompt</h2>
          <Textarea
            value={simulationPrompt}
            onChange={(e) => setSimulationPrompt(e.target.value)}
            placeholder="Define simulation goal and constraints..."
            rows={5}
            className="resize-none bg-white border-gray-300"
          />
        </div>

        {/* SECTION 5: SCRIPT CONFIGURATION */}
        <div className="space-y-4 border-t border-gray-200 pt-8">
          <h2 className="text-xl font-semibold text-black">Script Configuration</h2>
          <div className="space-y-4">
            <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="none"
                    id="none"
                    checked={scriptMode === "none"}
                    onChange={() => setScriptMode("none")}
                  />
                  <Label htmlFor="none" className="font-normal cursor-pointer text-black">
                    No Script (Persona-only)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="fixed"
                    id="fixed"
                    checked={scriptMode === "fixed"}
                    onChange={() => setScriptMode("fixed")}
                  />
                  <Label htmlFor="fixed" className="font-normal cursor-pointer text-black">
                    Fixed Script
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="checkpoint"
                    id="checkpoint"
                    checked={scriptMode === "checkpoint"}
                    onChange={() => setScriptMode("checkpoint")}
                  />
                  <Label htmlFor="checkpoint" className="font-normal cursor-pointer text-black">
                    Checkpoint Script
                  </Label>
                </div>
              </div>
            {scriptMode === "fixed" && (
              <Textarea
                value={fixedScript}
                onChange={(e) => setFixedScript(e.target.value)}
                placeholder="Enter fixed script utterances (one per line)..."
                rows={6}
                className="resize-none bg-white border-gray-300"
              />
            )}
            {scriptMode === "checkpoint" && (
              <Textarea
                value={checkpointScript}
                onChange={(e) => setCheckpointScript(e.target.value)}
                placeholder="Enter checkpoint milestones (one per line)..."
                rows={6}
                className="resize-none bg-white border-gray-300"
              />
            )}
          </div>
        </div>

        {/* SECTION 6: RUN SIMULATION */}
        <div className="space-y-4 pt-6 border-t border-gray-300">
          <h2 className="text-xl font-semibold text-black">Run Simulation</h2>
          {!isRunning && progress === 0 && (
            <Button
              onClick={handleRunSimulation}
              className="bg-black text-white hover:bg-black/90 w-full h-12 text-base"
              disabled={selectedPersonas.length === 0}
            >
              Run Simulation
            </Button>
          )}
          {isRunning && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-black font-medium">{simulationStatus || 'Running simulation...'}</span>
                <span className="text-black">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}
          {!isRunning && progress === 100 && (
            <div className="space-y-2">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-900 font-medium">{simulationStatus || 'Simulation completed successfully!'}</p>
                <p className="text-sm text-green-700 mt-1">View results in the Evals tab.</p>
              </div>
              <Button
                onClick={handleRunSimulation}
                variant="outline"
                className="bg-white border-black text-black w-full"
              >
                Run Again
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* PERSONA LIBRARY MODAL */}
      <Dialog open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
        <DialogContent className="max-w-[85vw] w-full sm:max-w-[1400px] max-h-[90vh] min-h-[70vh] overflow-hidden flex flex-col bg-white">
          <DialogHeader className="border-b border-gray-200 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <DialogTitle className="text-xl font-semibold text-black">
                  Persona Library ({filteredPersonas.length} personas)
                </DialogTitle>
                {checkedPersonas.size > 0 && (
                  <span className="text-sm text-gray-600">{checkedPersonas.size} selected</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search by persona name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-80 bg-white border-gray-300 text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                  className="bg-white border-gray-300 hover:bg-gray-50"
                  title="Toggle Filters"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 flex gap-6 overflow-hidden">
            {/* LEFT COLUMN - Persona List (35%) */}
            <div className="w-[35%] flex flex-col space-y-3 overflow-hidden">
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllVisible}
                    className="text-xs bg-white border-black hover:bg-gray-50"
                  >
                    Select All Visible
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearSelection}
                    className="text-xs bg-white border-black hover:bg-gray-50"
                    disabled={checkedPersonas.size === 0}
                  >
                    Unselect All
                  </Button>
                </div>
                <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                  <SelectTrigger className="w-36 h-8 text-xs bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Relevance</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="recent">Recently Added</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 overflow-y-auto border border-gray-300 rounded-lg">
                {filteredPersonas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <p className="text-gray-600 mb-3">No personas match your filters</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllFilters}
                      className="bg-white border-black text-black hover:bg-gray-50"
                    >
                      Clear Filters to See All Personas
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredPersonas.map((persona) => (
                      <div
                        key={persona.id}
                        className="flex items-start gap-3 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onMouseEnter={() => setHoveredPersona(persona)}
                        onClick={() => togglePersonaCheck(persona.id)}
                      >
                        <Checkbox
                          checked={checkedPersonas.has(persona.id)}
                          onCheckedChange={() => togglePersonaCheck(persona.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-black text-sm mb-1">{persona.name}</div>
                          <div className="text-xs text-gray-600 line-clamp-2">
                            {persona.category || (persona as any).description || ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CENTER COLUMN - Preview (45%) */}
            <div className="w-[45%] flex flex-col space-y-4 overflow-y-auto pr-4">
              <h3 className="font-semibold text-black text-lg sticky top-0 bg-white pb-3 border-b border-gray-200">
                Persona Preview
              </h3>
              {hoveredPersona ? (
                <div className="space-y-5">
                  <div>
                    <div className="font-semibold text-black text-lg mb-2">{hoveredPersona.name}</div>
                    <div className="text-sm text-gray-700 leading-relaxed">
                      {hoveredPersona.category || (hoveredPersona as any).description || ''}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Language</div>
                      <div className="text-sm text-black">{hoveredPersona.language}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Emotion</div>
                      <div className="text-sm text-black">
                        {hoveredPersona.emotionalState || (hoveredPersona as any).emotion || ''}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Intent</div>
                      <div className="text-sm text-black">{hoveredPersona.intent}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Risk Level</div>
                      <div className="text-sm text-black">
                        {hoveredPersona.riskLevel || (hoveredPersona as any).stress_level || ''}
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const riskData = (hoveredPersona as any).risk_flags || hoveredPersona.riskFlags
                    const riskFlags = Array.isArray(riskData) ? riskData : (typeof riskData === 'string' && riskData ? riskData.split(',').map(f => f.trim()) : [])
                    return riskFlags.length > 0 && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Risk Flags</div>
                        <div className="flex flex-wrap gap-2">
                          {riskFlags.map((flag: string) => (
                            <span key={flag} className="px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm">
                              {flag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  })()}

                  {(() => {
                    const sampleUtterances = (hoveredPersona as any).sample_utterances || []
                    return sampleUtterances.length > 0 && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Sample Utterances</div>
                        <div className="space-y-2">
                          {sampleUtterances.slice(0, 3).map((utterance: string, idx: number) => (
                            <div
                              key={idx}
                              className="text-sm text-black italic bg-gray-50 p-3 rounded-md border border-gray-200"
                            >
                              "{utterance}"
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })()}

                  {(() => {
                    const recommendedEvals = (hoveredPersona as any).recommended_evals || []
                    return recommendedEvals.length > 0 && (
                      <div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Recommended Eval Criteria</div>
                        <div className="flex flex-wrap gap-2">
                          {recommendedEvals.map((evalCriteria: string) => (
                            <span
                              key={evalCriteria}
                              className="px-3 py-1 bg-white border border-black text-black rounded-md text-sm"
                            >
                              {evalCriteria}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <p className="text-sm">Select a persona to preview details</p>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN - Filters Panel */}
            {isFilterPanelOpen && (
              <div className="w-[25%] flex flex-col space-y-4 overflow-y-auto pl-6 border-l border-gray-200">
                <h3 className="font-semibold text-black text-lg sticky top-0 bg-white pb-3">Filters</h3>

                {/* Language Filter */}
                <div className="space-y-2">
                  <button
                    onClick={() => setExpandedFilters((prev) => ({ ...prev, language: !prev.language }))}
                    className="flex items-center justify-between w-full text-sm font-medium text-black hover:text-gray-700"
                  >
                    <span>Language</span>
                    {expandedFilters.language ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFilters.language && (
                    <div className="space-y-2 pl-2">
                      {uniqueLanguages.slice(0, 8).map((lang) => (
                        <label key={lang} className="flex items-center space-x-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={filterLanguage.includes(lang)}
                            onCheckedChange={() => toggleFilter(filterLanguage, setFilterLanguage, lang)}
                          />
                          <span className="text-black">{lang}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Emotion Filter */}
                <div className="space-y-2">
                  <button
                    onClick={() => setExpandedFilters((prev) => ({ ...prev, emotion: !prev.emotion }))}
                    className="flex items-center justify-between w-full text-sm font-medium text-black hover:text-gray-700"
                  >
                    <span>Emotion</span>
                    {expandedFilters.emotion ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFilters.emotion && (
                    <div className="space-y-2 pl-2">
                      {uniqueEmotions.map((emotion) => (
                        <label key={emotion} className="flex items-center space-x-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={filterEmotion.includes(emotion)}
                            onCheckedChange={() => toggleFilter(filterEmotion, setFilterEmotion, emotion)}
                          />
                          <span className="text-black">{emotion}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Intent Filter */}
                <div className="space-y-2">
                  <button
                    onClick={() => setExpandedFilters((prev) => ({ ...prev, intent: !prev.intent }))}
                    className="flex items-center justify-between w-full text-sm font-medium text-black hover:text-gray-700"
                  >
                    <span>Intent</span>
                    {expandedFilters.intent ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFilters.intent && (
                    <div className="space-y-2 pl-2">
                      {uniqueIntents.slice(0, 8).map((intent) => (
                        <label key={intent} className="flex items-center space-x-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={filterIntent.includes(intent)}
                            onCheckedChange={() => toggleFilter(filterIntent, setFilterIntent, intent)}
                          />
                          <span className="text-black">{intent}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stress Level Filter */}
                <div className="space-y-2">
                  <button
                    onClick={() => setExpandedFilters((prev) => ({ ...prev, stress: !prev.stress }))}
                    className="flex items-center justify-between w-full text-sm font-medium text-black hover:text-gray-700"
                  >
                    <span>Stress Level</span>
                    {expandedFilters.stress ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFilters.stress && (
                    <div className="space-y-2 pl-2">
                      {["Low", "Medium", "High"].map((level) => (
                        <label key={level} className="flex items-center space-x-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={filterStressLevel.includes(level)}
                            onCheckedChange={() => toggleFilter(filterStressLevel, setFilterStressLevel, level)}
                          />
                          <span className="text-black">{level}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Risk Flags Filter */}
                {uniqueRiskFlags.length > 0 && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setExpandedFilters((prev) => ({ ...prev, risk: !prev.risk }))}
                      className="flex items-center justify-between w-full text-sm font-medium text-black hover:text-gray-700"
                    >
                      <span>Risk Flags</span>
                      {expandedFilters.risk ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {expandedFilters.risk && (
                      <div className="space-y-2 pl-2">
                        {uniqueRiskFlags.map((flag) => (
                          <label key={flag} className="flex items-center space-x-2 cursor-pointer text-sm">
                            <Checkbox
                              checked={filterRiskFlags.includes(flag)}
                              onCheckedChange={() => toggleFilter(filterRiskFlags, setFilterRiskFlags, flag)}
                            />
                            <span className="text-black">{flag}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="bg-white border-black text-black hover:bg-gray-50 mt-4"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between items-center pt-4 border-t border-gray-200 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setIsLibraryOpen(false)
                setIsCreatePersonaOpen(true)
              }}
              className="bg-white border-black text-black hover:bg-gray-50"
            >
              Create New Persona
            </Button>
            <Button
              onClick={addSelectedPersonas}
              disabled={checkedPersonas.size === 0}
              className="bg-black text-white hover:bg-black/90"
            >
              Add Selected Personas ({checkedPersonas.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CREATE NEW PERSONA MODAL */}
      <Dialog open={isCreatePersonaOpen} onOpenChange={setIsCreatePersonaOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col bg-white">
          <DialogHeader className="flex-shrink-0 border-b border-gray-200 pb-4">
            <DialogTitle className="text-2xl font-semibold text-black">Create New Persona</DialogTitle>
            <p className="text-sm text-gray-600 mt-1">
              Define a simulated patient archetype for call testing and evaluation
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 pr-2 py-4">
            {/* SECTION 1: BASIC IDENTITY */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Basic Identity</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="persona-name" className="text-black font-medium">
                    Persona Name <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="persona-name"
                    value={newPersona.name}
                    onChange={(e) => setNewPersona({ ...newPersona, name: e.target.value })}
                    className="bg-white border-gray-300"
                    placeholder="e.g., Confused Senior Citizen – Hindi"
                  />
                  <p className="text-xs text-gray-500">Must be unique within the Persona Library</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-black font-medium">
                    Short Description <span className="text-red-600">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={newPersona.description}
                    onChange={(e) => setNewPersona({ ...newPersona, description: e.target.value })}
                    className="bg-white border-gray-300 min-h-[70px]"
                    placeholder="Human-readable summary, e.g., 'Elderly caller who repeats questions and needs slow, patient guidance.'"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2: LANGUAGE & COMMUNICATION */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">
                Language & Communication
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="language" className="text-black font-medium">
                    Primary Language <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={newPersona.language}
                    onValueChange={(val) => setNewPersona({ ...newPersona, language: val })}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select primary language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Hindi">Hindi</SelectItem>
                      <SelectItem value="Hinglish">Hinglish</SelectItem>
                      <SelectItem value="Telugu">Telugu</SelectItem>
                      <SelectItem value="Tamil">Tamil</SelectItem>
                      <SelectItem value="Kannada">Kannada</SelectItem>
                      <SelectItem value="Malayalam">Malayalam</SelectItem>
                      <SelectItem value="Marathi">Marathi</SelectItem>
                      <SelectItem value="Bengali">Bengali</SelectItem>
                      <SelectItem value="Gujarati">Gujarati</SelectItem>
                      <SelectItem value="Punjabi">Punjabi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary-languages" className="text-black font-medium">
                    Secondary Language / Code-Switching
                  </Label>
                  <Input
                    id="secondary-languages"
                    value={newPersona.secondary_languages?.join(", ")}
                    onChange={(e) =>
                      setNewPersona({
                        ...newPersona,
                        secondary_languages: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    className="bg-white border-gray-300"
                    placeholder="e.g., English, Hindi (comma-separated)"
                  />
                  <p className="text-xs text-gray-500">Used for Hinglish / mixed-language personas</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="speaking-style" className="text-black font-medium">
                  Speaking Style
                </Label>
                <Input
                  id="speaking-style"
                  value={newPersona.speaking_style?.join(", ")}
                  onChange={(e) =>
                    setNewPersona({
                      ...newPersona,
                      speaking_style: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  className="bg-white border-gray-300"
                  placeholder="e.g., Slow, Pauses frequently, Interrupts (comma-separated)"
                />
                <p className="text-xs text-gray-500">
                  Options: Slow, Fast, Pauses frequently, Interrupts, Long explanations, Short answers
                </p>
              </div>
            </div>

            {/* SECTION 3: EMOTION & STRESS */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Emotion & Stress</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emotion" className="text-black font-medium">
                    Emotional State <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={newPersona.emotion}
                    onValueChange={(val) => setNewPersona({ ...newPersona, emotion: val })}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select emotional state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Calm">Calm</SelectItem>
                      <SelectItem value="Confused">Confused</SelectItem>
                      <SelectItem value="Anxious">Anxious</SelectItem>
                      <SelectItem value="Frustrated">Frustrated</SelectItem>
                      <SelectItem value="Angry">Angry</SelectItem>
                      <SelectItem value="Panicked">Panicked</SelectItem>
                      <SelectItem value="Distressed">Distressed</SelectItem>
                      <SelectItem value="Curious">Curious</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stress-level" className="text-black font-medium">
                    Stress Level <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={newPersona.stress_level}
                    onValueChange={(val) => setNewPersona({ ...newPersona, stress_level: val })}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* SECTION 4: INTENT & CONTEXT */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Intent & Context</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="intent" className="text-black font-medium">
                    Primary Intent <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={newPersona.intent}
                    onValueChange={(val) => setNewPersona({ ...newPersona, intent: val })}
                  >
                    <SelectTrigger className="bg-white border-gray-300">
                      <SelectValue placeholder="Select primary intent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Appointment Booking">Appointment Booking</SelectItem>
                      <SelectItem value="Follow-up Booking">Follow-up Booking</SelectItem>
                      <SelectItem value="Diagnostic Test Booking">Diagnostic Test Booking</SelectItem>
                      <SelectItem value="Teleconsultation">Teleconsultation</SelectItem>
                      <SelectItem value="Insurance / Billing Query">Insurance / Billing Query</SelectItem>
                      <SelectItem value="Home Healthcare">Home Healthcare</SelectItem>
                      <SelectItem value="Complaint">Complaint</SelectItem>
                      <SelectItem value="Emergency-like Symptoms">Emergency-like Symptoms</SelectItem>
                      <SelectItem value="Medical Advice Seeking">Medical Advice Seeking (Safety)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary-intent" className="text-black font-medium">
                    Secondary Intent
                  </Label>
                  <Input
                    id="secondary-intent"
                    value={newPersona.secondary_intent?.join(", ")}
                    onChange={(e) =>
                      setNewPersona({
                        ...newPersona,
                        secondary_intent: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    className="bg-white border-gray-300"
                    placeholder="e.g., Cost clarification (comma-separated)"
                  />
                  <p className="text-xs text-gray-500">Used for complex calls with multiple intents</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="medical-context" className="text-black font-medium">
                  Medical Context
                </Label>
                <Input
                  id="medical-context"
                  value={newPersona.medical_context?.join(", ")}
                  onChange={(e) =>
                    setNewPersona({
                      ...newPersona,
                      medical_context: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  className="bg-white border-gray-300"
                  placeholder="e.g., Cardiology, Pediatrics, Mental Health (comma-separated)"
                />
                <p className="text-xs text-gray-500">
                  Options: General OPD, Cardiology, Orthopedics, Pediatrics, Maternity, Mental Health, Diagnostics, Home
                  Care
                </p>
              </div>
            </div>

            {/* SECTION 5: RISK & SAFETY FLAGS */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Risk & Safety Flags</h3>
              <div className="space-y-2">
                <Label htmlFor="risk-flags" className="text-black font-medium">
                  Risk / Stress Flags
                </Label>
                <Input
                  id="risk-flags"
                  value={newPersona.risk_flags?.join(", ")}
                  onChange={(e) =>
                    setNewPersona({
                      ...newPersona,
                      risk_flags: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  className="bg-white border-gray-300"
                  placeholder="e.g., Emergency Indicators, Medical Advice Seeking (comma-separated)"
                />
                <p className="text-xs text-gray-500">
                  Options: Emergency Indicators, Medical Advice Seeking, Aggressive/Abusive Language, Low Digital
                  Literacy, Hearing Difficulty, Proxy Caller, Insurance Sensitivity, Escalation Risk, Jailbreak/AI
                  Probing
                </p>
              </div>
            </div>

            {/* SECTION 6: SAMPLE UTTERANCES */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Sample Utterances</h3>
              <div className="space-y-2">
                <Label htmlFor="utterances" className="text-black font-medium">
                  Sample Utterances <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="utterances"
                  value={newPersona.sample_utterances?.join("\n")}
                  onChange={(e) =>
                    setNewPersona({ ...newPersona, sample_utterances: e.target.value.split("\n").filter(Boolean) })
                  }
                  className="bg-white border-gray-300 min-h-[100px] font-mono text-sm"
                  placeholder="Add 2-5 sample utterances (one per line), e.g.:&#10;Mujhe appointment book karna hai, doctor kab available hai?&#10;Why are there no slots? This is not acceptable.&#10;Can you tell me if this is covered under insurance?"
                />
                <p className="text-xs text-gray-500">
                  Define how the persona actually speaks. Add 2-5 examples, one per line.
                </p>
              </div>
            </div>

            {/* SECTION 7: PATIENT SYSTEM PROMPT */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Patient System Prompt</h3>
              <div className="space-y-2">
                <Label htmlFor="system-prompt" className="text-black font-medium">
                  Patient System Prompt <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="system-prompt"
                  value={newPersona.patient_system_prompt}
                  onChange={(e) => setNewPersona({ ...newPersona, patient_system_prompt: e.target.value })}
                  className="bg-white border-gray-300 min-h-[120px] font-mono text-sm"
                  placeholder="Core simulation instruction, e.g.:&#10;&#10;You are an elderly patient who is confused and anxious. You speak primarily in Hindi, sometimes mixing English. You repeat questions if answers are unclear. You are calling to book an appointment and need patient guidance. Interrupt occasionally and ask for repetition."
                />
                <p className="text-xs text-gray-500">
                  Written as instructions to the patient-side AI. This is the core simulation instruction.
                </p>
              </div>
            </div>

            {/* SECTION 8: META (INTERNAL USE) */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black border-b border-gray-200 pb-2">Meta (Internal Use)</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="evals" className="text-black font-medium">
                    Recommended Eval Criteria
                  </Label>
                  <Input
                    id="evals"
                    value={newPersona.recommended_evals?.join(", ")}
                    onChange={(e) =>
                      setNewPersona({
                        ...newPersona,
                        recommended_evals: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                    className="bg-white border-gray-300"
                    placeholder="e.g., Appointment Booking Accuracy, Language Handling, Silence Handling (comma-separated)"
                  />
                  <p className="text-xs text-gray-500">
                    Options: Appointment Booking Accuracy, Language Handling, Silence Handling, Emergency Detection,
                    De-escalation, Insurance Clarity, Safety Refusal
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-black font-medium">
                    Notes
                  </Label>
                  <Textarea
                    id="notes"
                    value={newPersona.notes}
                    onChange={(e) => setNewPersona({ ...newPersona, notes: e.target.value })}
                    className="bg-white border-gray-300 min-h-[60px]"
                    placeholder="Internal notes for QA or auditors..."
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 justify-between pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => setIsCreatePersonaOpen(false)}
              className="bg-white border-black text-black hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePersona}
              disabled={
                !newPersona.name ||
                !newPersona.language ||
                !newPersona.emotion ||
                !newPersona.intent ||
                !newPersona.description ||
                !newPersona.sample_utterances?.length ||
                !newPersona.patient_system_prompt
              }
              className="bg-black text-white hover:bg-black/90 disabled:opacity-50"
            >
              Save Persona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

